import React, { useState } from 'react';
import { XIcon, UserIcon } from 'lucide-react';
interface EditProfileBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  userInfo: {
    name: string;
    email: string;
    phone: string;
    gender: string;
    maritalStatus: string;
    nationality: string;
    dateOfBirth: string;
    workingSector: string;
    city: string;
    district: string;
    preferredLanguage: string;
  };
  onSave?: (data: any) => void;
}
export function EditProfileBottomSheet({
  isOpen,
  onClose,
  userInfo,
  onSave
}: EditProfileBottomSheetProps) {
  const [formData, setFormData] = useState(userInfo);
  const nationalities = ['Saudi Arabian', 'Emirati', 'Kuwaiti', 'Qatari', 'Bahraini', 'Omani', 'Jordanian', 'Lebanese', 'Egyptian', 'Syrian', 'Iraqi', 'Yemeni', 'Palestinian', 'Libyan', 'Tunisian', 'Algerian', 'Moroccan', 'Sudanese', 'Other'];
  const workingSectors = ['Healthcare', 'Education', 'Technology', 'Finance', 'Government', 'Retail', 'Hospitality', 'Construction', 'Manufacturing', 'Transportation', 'Real Estate', 'Legal', 'Media', 'Agriculture', 'Energy', 'Telecommunications', 'Other'];
  const cities = ['Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Khobar', 'Dhahran', 'Tabuk', 'Buraidah', 'Khamis Mushait', 'Hofuf', 'Taif', 'Najran', 'Jubail', 'Abha', 'Yanbu', 'Al-Kharj', 'Qatif', 'Arar', 'Sakaka'];
  const districts = ['Al Olaya', 'Al Malqa', 'Al Nakheel', 'Al Yasmin', 'Al Sahafa', 'King Fahd', 'Al Wurud', 'Al Hamra', 'Al Rawdah', 'Al Muruj', 'Al Narjis', 'Granada', 'Hittin', 'Al Andalus', 'Al Sulimaniyah', 'Al Maather', 'Al Aqiq', 'Al Faisaliyah', 'Al Khaleej', 'Al Shifa'];
  const handleSave = () => {
    if (onSave) {
      onSave(formData);
    }
    onClose();
  };
  if (!isOpen) return null;
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity" onClick={onClose} data-id="element-321" data-mp="1hqckg98ssdcl" />
      {/* Bottom Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-[101] bg-white rounded-t-3xl shadow-2xl max-h-[90vh] overflow-hidden animate-slide-up" data-id="element-322" data-mp="13y668sp7ggfs">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2" data-id="element-323" data-mp="tz74zv6rrn2t">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-324" data-mp="u2l4mvo1ti6k" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10" data-id="element-325" data-mp="kzk214ye697n">
          <div className="flex items-center gap-3" data-id="element-326" data-mp="py5479x7f2yq">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1E2B55] to-[#2a3d6f] rounded-full flex items-center justify-center" data-id="element-327" data-mp="pywx2czf2mlb">
              <UserIcon className="w-5 h-5 text-white" data-id="element-328" data-mp="2kcefjlkyst9h" />
            </div>
            <div data-id="element-329" data-mp="2kalf4o0y359x">
              <h3 className="text-lg font-bold text-gray-900" data-id="element-330" data-mp="2ugmiin2bn523">Edit Profile</h3>
              <p className="text-xs text-gray-500" data-id="element-331" data-mp="2ueiapu1axlwj">Update your information</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-332" data-mp="2pfp1idt1sh26">
            <XIcon className="w-5 h-5 text-gray-500" data-id="element-333" data-mp="2yhybn2kem8f1" />
          </button>
        </div>
        {/* Content */}
        <div className="overflow-y-auto px-5 py-6" style={{
        maxHeight: 'calc(90vh - 180px)'
      }} data-id="element-334" data-mp="3d61c5wfogrwg">
          <div className="space-y-4" data-id="element-335" data-mp="38adt7yrsguze">
            {/* Full Name */}
            <div data-id="element-336" data-mp="3m793tvnouuhk">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-337" data-mp="3m5of4mo225rm">
                Full Name <span className="text-red-500" data-id="element-338" data-mp="3i42x6mwnz1ak">*</span>
              </label>
              <input type="text" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" placeholder="Enter your full name" data-id="element-339" data-mp="1rujabf18fnap" />
            </div>
            {/* Email */}
            <div data-id="element-340" data-mp="236qkc6hy1g3v">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-341" data-mp="16j3uirfdkvze">
                Email Address <span className="text-red-500" data-id="element-342" data-mp="1ak5u59d7fa1w">*</span>
              </label>
              <input type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" placeholder="your.email@example.com" data-id="element-343" data-mp="1aicitgrnc1nk" />
            </div>
            {/* Phone */}
            <div data-id="element-344" data-mp="fb29h3011uup">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-345" data-mp="fcn4etq97ft3">
                Phone Number <span className="text-red-500" data-id="element-346" data-mp="ab7kx63q5hct">*</span>
              </label>
              <input type="tel" value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" placeholder="+966 5X XXX XXXX" data-id="element-347" data-mp="6b6sghjsj5mr" />
            </div>
            {/* Gender */}
            <div data-id="element-348" data-mp="3ep5pznmeuz2d">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-349" data-mp="3epnqiwg3vzg3">
                Gender
              </label>
              <select value={formData.gender} onChange={e => setFormData({
              ...formData,
              gender: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-350" data-mp="303runmpinvnm">
                <option value="" data-id="element-351" data-mp="3itq1jzhkvipo">Select Gender</option>
                <option value="Male" data-id="element-352" data-mp="3iwcj8ry6nelm">Male</option>
                <option value="Female" data-id="element-353" data-mp="3ntm55xmd584p">Female</option>
              </select>
            </div>
            {/* Marital Status */}
            <div data-id="element-354" data-mp="393x19umvyq0q">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-355" data-mp="392c6c3wnt52c">
                Marital Status
              </label>
              <select value={formData.maritalStatus} onChange={e => setFormData({
              ...formData,
              maritalStatus: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-356" data-mp="5nzn7allsxx4">
                <option value="" data-id="element-357" data-mp="23w3ypvhtbqda">Select Status</option>
                <option value="Single" data-id="element-358" data-mp="1zvjygj1y06mx">Single</option>
                <option value="Married" data-id="element-359" data-mp="1zw3iq6f2f744">Married</option>
                <option value="Divorced" data-id="element-360" data-mp="1c5pm02ljck37">Divorced</option>
                <option value="Widowed" data-id="element-361" data-mp="1c6969pynrkke">Widowed</option>
              </select>
            </div>
            {/* Nationality */}
            <div data-id="element-362" data-mp="1l6nvv1innltp">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-363" data-mp="1l772hm2uunsf">
                Nationality
              </label>
              <select value={formData.nationality} onChange={e => setFormData({
              ...formData,
              nationality: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-364" data-mp="1g6t5r06pf09p">
                <option value="" data-id="element-365" data-mp="3glunfgzr520d">Select Nationality</option>
                {nationalities.map(nationality => <option key={nationality} value={nationality} data-id="element-366" data-mp="3pg8wv2xlpibk">
                    {nationality}
                  </option>)}
              </select>
            </div>
            {/* Date of Birth */}
            <div data-id="element-367" data-mp="38j4lulws9ka">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-368" data-mp="37zliakivf4o">
                Date of Birth
              </label>
              <input type="date" value={formData.dateOfBirth} onChange={e => setFormData({
              ...formData,
              dateOfBirth: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-369" data-mp="88x0kea8jt1y" />
            </div>
            {/* Working Sector */}
            <div data-id="element-370" data-mp="21ornbmh2frhf">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-371" data-mp="1wrrhnco4oqqu">
                Working Sector
              </label>
              <select value={formData.workingSector} onChange={e => setFormData({
              ...formData,
              workingSector: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-372" data-mp="1sqzacoytgvx7">
                <option value="" data-id="element-373" data-mp="2bge8aaj7h4xd">Select Sector</option>
                {workingSectors.map(sector => <option key={sector} value={sector} data-id="element-374" data-mp="14yios4fvelt5">
                    {sector}
                  </option>)}
              </select>
            </div>
            {/* City */}
            <div data-id="element-375" data-mp="52m49vy2zr4">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-376" data-mp="2gfccgvoo9y">
                City
              </label>
              <select value={formData.city} onChange={e => setFormData({
              ...formData,
              city: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-377" data-mp="8wwb8kpjhisb">
                <option value="" data-id="element-378" data-mp="rwa8bbikf9n1">Select City</option>
                {cities.map(city => <option key={city} value={city} data-id="element-379" data-mp="nu5eocs4ad0e">
                    {city}
                  </option>)}
              </select>
            </div>
            {/* District */}
            <div data-id="element-380" data-mp="2pma2bc2l2o5b">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-381" data-mp="2pmtmz0jmbqn1">
                District
              </label>
              <select value={formData.district} onChange={e => setFormData({
              ...formData,
              district: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-382" data-mp="2fimxvwz0ckpz">
                <option value="" data-id="element-383" data-mp="1vw730bm6tmlt">Select District</option>
                {districts.map(district => <option key={district} value={district} data-id="element-384" data-mp="1mt5ygfcz03k9">
                    {district}
                  </option>)}
              </select>
            </div>
            {/* Preferred Language */}
            <div data-id="element-385" data-mp="3h7gogr5ax3rm">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-386" data-mp="3h6xga25gdtqs">
                Preferred Language
              </label>
              <select value={formData.preferredLanguage} onChange={e => setFormData({
              ...formData,
              preferredLanguage: e.target.value
            })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-387" data-mp="3m5iexx5245d4">
                <option value="" data-id="element-388" data-mp="79yie1nwa9nw">Select Language</option>
                <option value="English" data-id="element-389" data-mp="79f02juc1j9c">English</option>
                <option value="Arabic" data-id="element-390" data-mp="2avdqibtu5gg">Arabic</option>
              </select>
            </div>
          </div>
        </div>
        {/* Footer Actions */}
        <div className="flex gap-3 px-5 py-4 border-t border-gray-200 bg-white sticky bottom-0" data-id="element-391" data-mp="3tnq1ypr3jv55">
          <button onClick={onClose} className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors" data-id="element-392" data-mp="3ooltk1bzy1fv">
            Cancel
          </button>
          <button onClick={handleSave} className="flex-1 px-4 py-3 bg-[#1E2B55] text-white font-semibold rounded-xl hover:bg-[#162041] transition-colors" data-id="element-393" data-mp="slnoyf2mfc5y">
            Save Changes
          </button>
        </div>
      </div>
      <style data-id="element-394" data-mp="12lelsnenm397">{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>;
}